/** Browser installation choices, using the same bounded menus as the game. */
import type { InstallPrompt } from "./install";
import { ListMenu } from "./menu";
import { exitFullscreen, isIos } from "./touch";

/** Browser names in a user agent select help text only; the native prompt stays capability-based. */
function installInstructions(): string[] {
  const ua = navigator.userAgent;
  const embedded = /FBAN|FBAV|Instagram|\bGSA\/|\bLine\/|;\s*wv[;)]/i.test(ua);
  const chrome = !embedded && /\b(?:CriOS|Chrome)\//.test(ua);
  const firefox = !embedded && /\b(?:FxiOS|Firefox)\//.test(ua);
  const edge = !embedded && /\b(?:Edg|EdgA|EdgiOS)\//.test(ua);
  const samsung = !embedded && /\bSamsungBrowser\//.test(ua);
  const safari =
    !embedded &&
    /\bVersion\/.*\bSafari\//.test(ua) &&
    !chrome &&
    !firefox &&
    !edge &&
    !samsung &&
    !/\b(?:OPiOS|OPR|Vivaldi)\//.test(ua);

  if (isIos()) {
    const first = chrome
      ? "In Chrome, tap Share beside the address bar."
      : firefox
        ? "In Firefox, tap Share in the address bar."
        : safari
          ? "In Safari, tap Share (the square with an up arrow). It may be under More (…)."
          : "Open your browser’s menu and look for Share.";
    const steps = [
      first,
      "Choose Add to Home Screen.",
      "Confirm the name, keep Open as Web App on if shown, then tap Add.",
    ];
    if (!chrome && !firefox && !safari) {
      steps.push(
        "If that option is missing, open Lache in Safari or Chrome and use Share → Add to Home Screen.",
      );
    }
    return steps;
  }

  if (/Android/i.test(ua)) {
    if (firefox) {
      return [
        "In Firefox, open the menu and tap Install.",
        "Tap Add automatically or confirm Add to Home screen.",
        "Open Lache from your Home Screen.",
      ];
    }
    if (edge) {
      return [
        "In Edge, open the menu and choose Add to Phone.",
        "Follow the installation prompts.",
        "Open Lache from your Home Screen.",
      ];
    }
    if (samsung) {
      return [
        "In Samsung Internet, open the menu and choose Add page to → Home screen.",
        "Confirm Add or Install when asked.",
        "Open Lache from your Home Screen.",
      ];
    }
    if (chrome && !/\b(?:OPR|Vivaldi)\//.test(ua)) {
      return [
        "In Chrome, tap the ⋮ menu.",
        "Choose Install and create shortcut → Install, or Install app / Add to Home screen.",
        "Confirm installation, then open Lache from your Home Screen.",
      ];
    }
    return [
      "Open your browser’s menu and look for Install app or Add to Home screen.",
      "Confirm installation, then open Lache from your Home Screen.",
      "If that option is missing, open Lache in Chrome and use its ⋮ menu.",
    ];
  }

  if (safari && /Macintosh/.test(ua)) {
    return [
      "In Safari, choose File → Add to Dock.",
      "Confirm the name and click Add.",
      "Open Lache from the Dock.",
    ];
  }
  if (edge) {
    return [
      "In Edge, open the … menu, then More tools → Apps → Install this site as an app.",
      "Click Install.",
      "Open Lache from its new app icon.",
    ];
  }
  if (chrome && !/\b(?:OPR|Vivaldi)\//.test(ua)) {
    return [
      "In Chrome, click the Install icon beside the address bar.",
      "Follow the install prompt.",
      "Open Lache from its new app icon.",
    ];
  }
  return [
    "Look for Install in your browser’s address bar or menu.",
    "If it is unavailable, open Lache in Chrome or Edge. On Mac, Safari has File → Add to Dock.",
    "Open Lache from its new app icon.",
  ];
}

export class InstallScreen {
  readonly menu: ListMenu;
  private readonly back: HTMLButtonElement;
  private done: (() => void) | null = null;
  private firstChoice = false;
  private instructions = false;
  private generation = 0;

  constructor(
    parent: HTMLElement,
    private readonly install: InstallPrompt,
    private readonly present: (menu: ListMenu, open: () => void) => void,
  ) {
    this.menu = new ListMenu(parent, {
      className: "install-menu",
      title: "Play Lache",
      hint: "Escape to go back",
      touchHint: "",
      back: "Back",
      onCancel: () => this.goBack(),
    });
    this.back = this.menu.el.querySelector<HTMLButtonElement>(".menu-back")!;
    // Keep a focused header button's native Enter/Space from also choosing the list row.
    this.back.addEventListener("keydown", (event) => {
      if (event.key === "Enter" || event.key === " ") event.stopPropagation();
    });
    this.back.addEventListener("keyup", (event) => {
      if (event.key === "Enter" || event.key === " ") event.stopPropagation();
    });
    install.subscribe(() => {
      if (install.installed && this.menu.open) this.finish();
      const primary = this.menu.el.querySelector<HTMLButtonElement>(".items button");
      if (primary && !this.instructions) primary.disabled = install.busy;
    });
  }

  /** Every ordinary browser launch offers installation before username setup or a deep link. */
  offer(done: () => void): void {
    this.done = done;
    this.firstChoice = true;
    this.generation++;
    this.choices();
  }

  /** Main-menu/Pause buttons go directly to the native prompt or platform instructions. */
  open(done: () => void): void {
    this.done = done;
    this.firstChoice = false;
    this.generation++;
    void this.request();
  }

  private summary(text: string): HTMLDivElement {
    const summary = document.createElement("div");
    summary.className = "install-summary";
    const icon = document.createElement("img");
    icon.src = "/icons/icon-192.png";
    icon.alt = "";
    icon.width = 64;
    icon.height = 64;
    const paragraph = document.createElement("p");
    paragraph.textContent = text;
    summary.append(icon, paragraph);
    return summary;
  }

  private choices(): void {
    this.instructions = false;
    this.menu.setTitle("Play Lache");
    this.menu.content.replaceChildren(this.summary("Add Lache to your Home Screen for full screen play."));
    this.back.hidden = true;
    this.menu.setItems([
      { label: "Install (full screen)", action: () => void this.request() },
      { label: "Play in browser", action: () => this.finish() },
    ]);
    this.present(this.menu, () => this.menu.show(0));
  }

  private async request(): Promise<void> {
    if (this.install.busy) return;
    if (this.install.installed) {
      this.finish();
      return;
    }
    if (!this.install.available) {
      this.guide();
      return;
    }
    const generation = this.generation;
    // No await before request: the browser's prompt must run in this tap/key's activation.
    const outcome = await this.install.request();
    if (generation !== this.generation) return;
    if (outcome === "accepted") this.finish();
    else if (outcome === "failed" || outcome === "unavailable") this.guide();
    else if (!this.firstChoice) this.finish();
  }

  private guide(): void {
    this.instructions = true;
    // Instructions refer to the browser's Share/menu controls, which fullscreen hides.
    exitFullscreen();
    this.menu.setTitle("Add to Home Screen");
    this.back.hidden = false;
    const steps = document.createElement("ol");
    steps.className = "install-steps";
    for (const text of installInstructions()) {
      const step = document.createElement("li");
      step.textContent = text;
      steps.appendChild(step);
    }
    this.menu.content.replaceChildren(steps);
    this.menu.setItems([{ label: "Play in browser", action: () => this.finish() }]);
    this.menu.content.scrollTop = 0;
    this.present(this.menu, () => this.menu.show(0));
  }

  private goBack(): void {
    if (this.firstChoice && this.instructions) this.choices();
    else this.finish();
  }

  private finish(): void {
    const done = this.done;
    this.done = null;
    this.generation++;
    done?.();
  }
}
